// Hooks do domínio de USUÁRIOS (os membros da organização ativa). Mesmo padrão
// de use-brands.ts / use-calls.ts:
//   - `src/api/users.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas pelas telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, então
// uma chave que divergisse uma letra viraria dado velho na tela sem erro nenhum.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrgUser,
  getOrgUsers,
  inviteOrgUser,
  removeOrgUser,
  resetOrgUserPassword,
  updateOrgUserHubspotOwner,
  updateOrgUserName,
  updateOrgUserRole,
  type OrgUsersResponse,
} from "@/api/users";
import { invalidateAppAccess } from "@/lib/auth-client";
import { onboardingKeys } from "./use-onboarding";

// A chave DESTE domínio. É uma só: GET /v1/users devolve a org inteira (lista
// de membros + papel de quem vê) e as dez telas que a consultam dividem a MESMA
// entrada de cache de propósito — é o que faz a lista de membros custar um
// request por sessão, e não um por tela.
//
// Duas ressalvas herdadas:
//  - o nome da chave é "org-users" e o do endpoint é /v1/users. Divergência
//    pré-existente, preservada: renomear aqui não quebraria teste nenhum, só
//    faria as telas deixarem de compartilhar cache com quem não migrou.
//  - a jornada NÃO é deste domínio, mas criar/convidar usuário fecha o passo
//    "Convide o time" dela — a consequência é a mesma para todo chamador, então
//    a invalidação é do hook. A chave morava aqui literal, como `userKeys.journey`,
//    com o aviso de que iria embora quando o domínio de onboarding saísse: ele
//    saiu, e o invalidador abaixo aponta para `onboardingKeys.journey`.
export const userKeys = {
  orgUsers: ["org-users"] as const,
};

// Membros da organização ativa + o papel de quem está vendo (`viewerRole`).
// `enabled` existe para os diálogos que só buscam quando abrem (o seletor de
// responsável de um lead, por exemplo) — sem ele a lista viria em toda tela que
// apenas monta o diálogo fechado.
export function useOrgUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: userKeys.orgUsers,
    queryFn: getOrgUsers,
    enabled: options.enabled,
  });
}

// Convidar e criar mexem no MESMO par de consultas (a lista e a jornada), então
// a invalidação é do hook. Os `handlers` entram como parâmetro, e não via
// `mutate(vars, { onSuccess })`, para a ORDEM original ser preservada (tela
// primeiro — toast, reset, fechar —, invalidação depois).
function useInvalidateAfterMemberWrite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: userKeys.orgUsers });
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.journey });
  };
}

// POST /v1/users/invite. Os `mutationFn` deste arquivo chamam a função de api
// dentro de uma seta, em vez de passá-la direta: o TanStack invoca o mutationFn
// com (variáveis, contexto), e uma função de api que aceite um segundo
// parâmetro opcional receberia o contexto da mutação como se fosse dado.
export function useInviteOrgUser(
  handlers: {
    onSuccess?: (data: { invitationId: string; email: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const invalidate = useInvalidateAfterMemberWrite();
  return useMutation({
    mutationFn: (input: { email: string; roleId: string }) => inviteOrgUser(input),
    onSuccess: (data) => {
      handlers.onSuccess?.(data);
      invalidate();
    },
    onError: handlers.onError,
  });
}

// POST /v1/users — cria o usuário direto na org, com senha inicial no retorno.
export function useCreateOrgUser(
  handlers: {
    onSuccess?: (data: {
      userId: string;
      memberId: string;
      role: string;
      created: boolean;
      initialPassword?: string;
    }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const invalidate = useInvalidateAfterMemberWrite();
  return useMutation({
    mutationFn: (input: { name: string; email: string; roleId: string }) => createOrgUser(input),
    onSuccess: (data) => {
      handlers.onSuccess?.(data);
      invalidate();
    },
    onError: handlers.onError,
  });
}

// O que a ficha do membro salva de uma vez: nome, papel e o vínculo com o owner
// do HubSpot. Campo AUSENTE = não mudou e nem é chamado; a tela é quem decide
// isso (é ela que sabe o que o gestor digitou).
export interface OrgUserProfilePatch {
  name?: string;
  roleId?: string;
  hubspotOwnerId?: string | null;
}

// PATCH em série na ordem original (renomeia, troca o papel, liga o owner) — a
// ordem importa e por isso não virou Promise.all. Trocar o papel invalida
// TAMBÉM o snapshot de acesso: quem mexe na própria linha continuaria navegando
// com o papel antigo por até 5 minutos (o cache SWR de getAppAccess não é
// TanStack Query). Mesmo motivo do use-roles.ts.
export function useUpdateOrgUserProfile(
  memberId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: OrgUserProfilePatch) => {
      if (patch.name !== undefined) await updateOrgUserName(memberId, patch.name);
      if (patch.roleId !== undefined) await updateOrgUserRole(memberId, patch.roleId);
      if (patch.hubspotOwnerId !== undefined)
        await updateOrgUserHubspotOwner(memberId, patch.hubspotOwnerId);
    },
    onSuccess: (_data, patch) => {
      handlers.onSuccess?.();
      void queryClient.invalidateQueries({ queryKey: userKeys.orgUsers });
      if (patch.roleId !== undefined) invalidateAppAccess();
    },
    onError: handlers.onError,
  });
}

// Desfazer o vínculo com o HubSpot é ação própria, não "salvar campo vazio" —
// daí o hook separado do de cima, mandando `null` explícito.
export function useUnlinkHubspotOwner(
  memberId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => updateOrgUserHubspotOwner(memberId, null),
    onSuccess: () => {
      handlers.onSuccess?.();
      void queryClient.invalidateQueries({ queryKey: userKeys.orgUsers });
    },
    onError: handlers.onError,
  });
}

// DELETE /v1/users/:memberId, com remoção OTIMISTA da linha. A cirurgia no
// cache mora aqui (e não na tela) porque ela é a mesma para qualquer chamador:
// cancela o refetch em voo para ele não recolocar a linha, guarda o snapshot
// exato para o rollback e devolve a lista ao servidor no onSettled.
export function useRemoveOrgUser(
  memberId: string,
  handlers: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => removeOrgUser(memberId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: userKeys.orgUsers });
      const previous = queryClient.getQueryData<OrgUsersResponse>(userKeys.orgUsers);
      queryClient.setQueryData<OrgUsersResponse>(userKeys.orgUsers, (current) =>
        current
          ? { ...current, members: current.members.filter((item) => item.memberId !== memberId) }
          : current,
      );
      return { previous };
    },
    onSuccess: () => handlers.onSuccess?.(),
    onError: (error: Error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(userKeys.orgUsers, context.previous);
      handlers.onError?.(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.orgUsers });
      handlers.onSettled?.();
    },
  });
}

// Redefinir senha não mexe em nada que esteja em cache (a senha só existe na
// resposta) — por isso sem invalidação.
export function useResetOrgUserPassword(
  memberId: string,
  handlers: {
    onSuccess?: (data: { initialPassword: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: () => resetOrgUserPassword(memberId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}
